// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Button, Typography } from '@material-ui/core';
import { toast } from 'react-toastify';

// Internal Dependencies
import OverLoading from '../../global/OverLoading';
import PartnerBuildingChips from './PartnerBuildingChips';
import { IsEmpty } from '../../../common/checks';
import apiService from '../../../services.js/apiService';

const PartnerBuildings = ({ match }) => {
  const pageId = match.params.id;

  const [loading, setLoading] = useState(true);
  const [partnerBuildingData, setPartnerBuildingData] = useState([]);
  const [hasData, setHasData] = useState(false);

  const formatAddresses = (addresses) => {
    const formattedAddresses = [];

    addresses.forEach((address) => {
      const indexOfAddress = formattedAddresses.findIndex((e) => e.name === address.name);
      const hasPostalCode = !IsEmpty(address.postalCode);

      if (indexOfAddress === -1) {
        formattedAddresses.push({
          name:        address.locatedIn,
          postalCodes: [address.postalCode],
          hasPostalCode,
        });
      } else {
        // check postalCode
        if (!formattedAddresses[indexOfAddress].hasPostalCode) {
          formattedAddresses[indexOfAddress].hasPostalCode = hasPostalCode;
        }

        formattedAddresses[indexOfAddress].postalCodes.push(address.postalCode);
      }
    });

    return formattedAddresses;
  };

  const getPartnerBuildingDataHandler = async () => {
    try {
      setLoading(true);
      const partnerBuildings = await apiService.getPartnerBuildings(pageId);
      const formattedPartnerBuildings = formatAddresses(partnerBuildings);

      setPartnerBuildingData(formattedPartnerBuildings);
      setHasData(!IsEmpty(formattedPartnerBuildings));
    } catch (error) {
      toast.error('Error while fetching data for partner buildings');
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const onSaveHandler = async () => {
    try {
      setLoading(true);
      const formattedAddresses = _.map(partnerBuildingData, (partnerBuilding) => {
        const postalCodes = _.filter(partnerBuilding.postalCodes, (postalCode) => !IsEmpty(postalCode));
        return { name: partnerBuilding.name, postalCodes };
      });

      // If an address has no "postalCode", then that needs to be removed and won't be saved in database
      const emptyPostalCodeAddressNames = _.filter(formattedAddresses, (address) => _.isEmpty(address.postalCodes))
        .map((address) => address.name);

      if (!_.isEmpty(emptyPostalCodeAddressNames)) {
        return toast.error(`Please remove addresses with names "${emptyPostalCodeAddressNames.join(', ')}" because they don't have postal codes`);
      }

      const payload = { pageId: Number(pageId), addresses: formattedAddresses };
      if (!hasData) { // CREATE
        await apiService.createPartnerBuildings(pageId, payload);
        toast.success('New addresses added for this partner buildings');
      } else { // UPDATE
        await apiService.updatePartnerBuildings(pageId, payload);
        toast.success('Updated addresses for this partner buildings');
      }

      // fetch latest data
      getPartnerBuildingDataHandler();
    } catch (error) {
      toast.error(`Error while saving data for partner buildings: ${error.message}`);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPartnerBuildingDataHandler();
  }, []);

  return (
    <div className="container">
      { loading && <OverLoading /> }
      <div className="w-100 mb-3">
        <Typography variant="h6" color="inherit" className="">
          Partner Buildings
        </Typography>
        <Typography variant="subtitle1" color="inherit" className="">
          Enter the mall/building within the geographic boundary that are under this Area Management
        </Typography>

        <PartnerBuildingChips
          addedAddresses={partnerBuildingData}
          setAddedAddresses={setPartnerBuildingData}
        />

        <div className="d-flex justify-content-end mt-3 mb-3">
          <Button onClick={onSaveHandler} variant="contained" color="primary">
            {hasData ? 'Update' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PartnerBuildings;

PartnerBuildings.propTypes = {
  match: PropTypes.shape(Object).isRequired,
};
